import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, CheckCircle2, FileSignature, RefreshCw } from 'lucide-react';
import { Overview, operatorApi, useFetch } from '../../services/operatorApi';
import { Badge, Button, Card, DocumentStatusBadge, Empty, ErrorBox, PageHeader, Spinner, StatCard, ago, dateTime, documentStatusLabel, eur, num } from '../../components/ui';

const PERIODS = [7, 30, 90];

const EVENT_LABELS: Record<string, string> = {
  'document.sent': 'Document envoyé',
  'document.completed': 'Document signé et scellé',
  'document.declined': 'Document refusé',
  'document.voided': 'Document annulé',
  'signer.signed': 'Signature apposée',
  'signer.declined': 'Signature refusée',
  'document.created': 'Document créé',
};

const shortDay = (value: string) => value.slice(8, 10) + '/' + value.slice(5, 7);

export default function DashboardPage() {
  const [days, setDays] = useState(30);
  const { data, error, loading, reload } = useFetch(async () => (await operatorApi.get<Overview>('/overview', { params: { days } })).data, [days]);

  if (loading && !data) return <Spinner />;
  if (error && !data) return <ErrorBox message={error} onRetry={reload} />;
  if (!data) return null;

  const { money, period, attention, clients } = data;
  const trend = period.signatures_trend;
  const alerts: { tone: 'red' | 'amber'; text: string; to: string }[] = [
    ...attention.negative_balance.map((c) => ({ tone: 'red' as const, text: `${c.name} a un solde négatif (${c.balance})`, to: `/clients/${c.id}` })),
    ...attention.sealing_stuck.map((d) => ({ tone: 'red' as const, text: `Scellement bloqué : « ${d.title} » (${d.client})`, to: `/documents/${d.id}` })),
    ...attention.low_balance.map((c) => ({ tone: 'amber' as const, text: `${c.name} est à recharger : ${c.available} signature(s) disponible(s)`, to: `/clients/${c.id}` })),
    ...(attention.failed_webhooks_24h > 0 ? [{ tone: 'amber' as const, text: `${attention.failed_webhooks_24h} webhook(s) en échec sur 24 h`, to: '/system' }] : []),
    ...(attention.expiring_soon > 0 ? [{ tone: 'amber' as const, text: `${attention.expiring_soon} document(s) expirent dans moins de 3 jours`, to: '/documents?status=sent' }] : []),
    ...(attention.email_suppressions && attention.email_suppressions.recent > 0
      ? [{ tone: 'amber' as const, text: `${attention.email_suppressions.recent} adresse(s) e-mail en rebond cette semaine`, to: '/system' }]
      : []),
  ];

  const statusOrder = ['draft', 'sent', 'in_progress', 'completed', 'declined', 'expired', 'voided'];
  const byStatus = [...data.documents.by_status].sort((a, b) => statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status));
  const totalDocuments = byStatus.reduce((sum, s) => sum + s.count, 0);

  return (
    <div>
      <PageHeader
        title="Tableau de bord"
        subtitle="Activité de la plateforme sur la période choisie"
        actions={
          <>
            <div className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5">
              {PERIODS.map((p) => (
                <button key={p} onClick={() => setDays(p)} className={`rounded-md px-3 py-1.5 text-xs font-medium ${days === p ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                  {p} j
                </button>
              ))}
            </div>
            <Button variant="secondary" onClick={reload} loading={loading}>
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </Button>
          </>
        }
      />

      {alerts.length > 0 ? (
        <div className="mb-6 space-y-2">
          {alerts.map((alert, index) => (
            <Link
              key={index}
              to={alert.to}
              className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-sm ${alert.tone === 'red' ? 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100' : 'border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100'}`}
            >
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {alert.text}
            </Link>
          ))}
        </div>
      ) : (
        <div className="mb-6 flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <CheckCircle2 className="w-4 h-4" />
          Rien à signaler : aucun scellement bloqué, aucun client à recharger.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Signatures"
          value={num(period.signatures)}
          icon={<FileSignature className="w-4 h-4" />}
          hint={
            trend === null ? (
              `sur ${days} jours`
            ) : (
              <span className={`inline-flex items-center gap-1 ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(trend)} % vs période précédente
              </span>
            )
          }
        />
        <StatCard label="Ventes de lots" value={eur(money.sales_period_cents)} hint={`${eur(money.sales_total_cents)} depuis le début`} />
        <StatCard
          label="Marge estimée"
          value={eur(money.estimated_margin_cents)}
          tone={money.estimated_margin_cents >= 0 ? 'good' : 'bad'}
          hint={`${eur(money.recognized_revenue_cents)} consommés − ${eur(money.estimated_costs_cents)} de coûts`}
        />
        <StatCard label="Clients actifs" value={`${clients.active} / ${clients.total}`} hint={`${clients.prepaid} en lots prépayés`} />
        <StatCard label="Documents créés" value={num(period.documents_created)} hint={`${num(period.documents_completed)} signés, ${num(period.documents_declined)} refusés`} />
        <StatCard label="Taux de complétion" value={data.documents.completion_rate === null ? '—' : `${data.documents.completion_rate} %`} hint="signés sur terminés (signés, refusés, expirés)" />
        <StatCard label="Crédits en circulation" value={num(money.credits_outstanding)} hint={`${num(money.credits_consumed_period)} consommés sur la période`} />
        <StatCard label="Coûts estimés" value={eur(money.estimated_costs_cents)} hint={`dont ${num(period.sms_signatures)} signature(s) avec SMS`} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card title="Signatures et documents par jour" className="xl:col-span-2">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.series} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="sig" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#4f46e5" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="day" tickFormatter={shortDay} tick={{ fontSize: 11, fill: '#64748b' }} interval="preserveStartEnd" minTickGap={24} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                <Tooltip labelFormatter={(v) => shortDay(String(v))} />
                <Area type="monotone" dataKey="signatures" name="Signatures" stroke="#4f46e5" strokeWidth={2} fill="url(#sig)" />
                <Area type="monotone" dataKey="documents" name="Documents créés" stroke="#0ea5e9" strokeWidth={2} fill="none" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Ventes de lots par jour">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.series.map((p) => ({ ...p, sales: p.sales_cents / 100 }))} margin={{ top: 5, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="day" tickFormatter={shortDay} tick={{ fontSize: 11, fill: '#64748b' }} interval="preserveStartEnd" minTickGap={24} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                <Tooltip labelFormatter={(v) => shortDay(String(v))} formatter={(v) => [`${v} €`, 'Ventes']} />
                <Bar dataKey="sales" fill="#10b981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card title="Documents par statut">
          {totalDocuments === 0 ? (
            <Empty>Aucun document pour l&apos;instant.</Empty>
          ) : (
            <ul className="space-y-3">
              {byStatus.map((s) => (
                <li key={s.status}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <Link to={`/documents?status=${s.status}`} className="text-slate-700 hover:text-indigo-600">{documentStatusLabel(s.status)}</Link>
                    <span className="tabular-nums text-slate-500">{num(s.count)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100">
                    <div className="h-1.5 rounded-full bg-indigo-500" style={{ width: `${Math.max(2, (s.count / totalDocuments) * 100)}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={`Meilleurs clients (${days} j)`}>
          {data.top_clients.length === 0 ? (
            <Empty>Aucune signature sur la période.</Empty>
          ) : (
            <ol className="space-y-3">
              {data.top_clients.map((c, index) => (
                <li key={c.id} className="flex items-center justify-between text-sm">
                  <Link to={`/clients/${c.id}`} className="flex items-center gap-3 text-slate-800 hover:text-indigo-600">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-medium text-slate-600">{index + 1}</span>
                    {c.name}
                  </Link>
                  <Badge tone="violet">{num(c.signatures)} signature(s)</Badge>
                </li>
              ))}
            </ol>
          )}
        </Card>

        <Card title="Activité récente">
          {data.recent_activity.length === 0 ? (
            <Empty>Aucune activité.</Empty>
          ) : (
            <ul className="space-y-3">
              {data.recent_activity.map((event, index) => (
                <li key={index} className="text-sm">
                  <Link to={`/documents/${event.document_id}`} className="block hover:text-indigo-600">
                    <span className="font-medium text-slate-800">{EVENT_LABELS[event.type] ?? event.type}</span>
                    <span className="block truncate text-xs text-slate-500">
                      {event.title} · {event.client} · <span title={dateTime(event.occurred_at)}>{ago(event.occurred_at)}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {byStatus.some((s) => s.status === 'sent' || s.status === 'in_progress') && (
        <p className="mt-6 text-xs text-slate-500">
          Les documents « <DocumentStatusBadge status="sent" /> » ou « <DocumentStatusBadge status="in_progress" /> » attendent une ou plusieurs signatures.
        </p>
      )}
    </div>
  );
}
